import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../../services/profile/profile-data.service';
import { UserProfileShareService } from '../../../../services/shared-service/user-profile-share.service';
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from '../../../../services/resources/resources-data.service';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { Store } from '@ngxs/store';
import { CLOUD_DATA_SERVICE, CloudDataService } from '../../../../services/cloud/cloud-data.service';
import { AdminMandant, OrgUnitSetting } from '../../../../models/cloud/cloud.model';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import { LeadType } from '../../../../models/leads/leads.model';
import { ContactType } from '../../../../models/contacts/contacts.model';
import { HideLoader, ShowLoader } from '../../../store/loader/loader.actions';
import { ActionHelper } from '../../../../helper/action.helper';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'fi-organizational-units',
    templateUrl: './organizational-units.component.html',
    styleUrls: ['./organizational-units.component.scss'],
  standalone: false,
})
export class OrganizationalUnitsComponent implements OnInit, OnChanges {
    @Input() mandant?: AdminMandant;
    orgUnitSettings: OrgUnitSetting[] = [];
    parentDropdownOptions: OrgUnitSetting[] = [];
    edittedOrgUnitSetting? :OrgUnitSetting;
    leadTypes: LeadType[] =[];
    contactTypes: ContactType[] = [];

    selectedLeadTypes: LeadType[] =[];
    selectedContactTypes: ContactType[] = [];

    selectedLeadsText = '';
    selectedContactsText = '';
    selectedParentText = '';

    nameError = false;

    constructor(
        private readonly sharedProfileService: UserProfileShareService,
        @Inject(PROFILE_DATA_SERVICE) private readonly profileDataService: ProfileDataService,
        @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
        @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
        @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
        @Inject(ACTION_DATA_SERVICE) public actionDataService: ActionDataService,
        @Inject(CLOUD_DATA_SERVICE) public cloudDataService: CloudDataService,
        private dialog: MatDialog,
        private fb: UntypedFormBuilder,
        private store: Store
    ) {
        leadsDataService.getLeadTypes().subscribe(({ success, data }) => {
            if (success) {
                this.leadTypes = data.filter(item => !item.Inactive);
            }
        });

        contactsDataService.getContactTypes().subscribe(({ success, data }) => {
            if (success) {
                this.contactTypes = data;
            }
        });
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.mandant?.currentValue) {
            this.getOrgUntSettings();
        }
    }

    getOrgUntSettings(): void {
        this.cloudDataService.getOrgUnitSettings(this.mandant.Id).subscribe(({success, data}) => {
            if (data) {
                this.orgUnitSettings = data;
            }
        })
    }

    onEdit(idx: number): void {
        const setting = this.orgUnitSettings[idx];
        this.edittedOrgUnitSetting = {
            ...setting,
            AllowedLeadTypeIds: [...setting.AllowedLeadTypeIds],
            DisallowedContactTypeIds: [...setting.DisallowedContactTypeIds],
        }

        this.selectedContactTypes = this.contactTypes.filter(x => !setting.DisallowedContactTypeIds.includes(x.Id));
        this.selectedLeadTypes = this.leadTypes.filter(x => setting.AllowedLeadTypeIds.includes(x.Id));
        this.updateSelectionTexts();
        this.setParentOptions();
        this.nameError = false;
    }

    onCreate(): void {
        this.edittedOrgUnitSetting = {
            AllowedLeadTypeIds: [],
            Description: '',
            DisallowedContactTypeIds: this.contactTypes.map(x => x.Id),
            IsContactManagerOU: false,
            IsFinstral: false,
            IsMandantAdminOU: false,
            MandantId: this.mandant.Id,
            Name: '',
            SharedRead: false
        };

        this.selectedContactTypes = [];
        this.selectedLeadTypes = [];
        this.updateSelectionTexts();
        this.setParentOptions();
        this.nameError = false;
    }

    onDelete(i: number): void {
        ActionHelper.showAction('DELETE-CONFIRM', this.dialog, {
            customMessageSnippet: 'ADMINISTRATION_OU_MESSAGE_DELETION',
            customConfirmSnippet: 'ADMINISTRATION_BUTTON_CONFIRM',
            customCancelSnippet: 'ADMINISTRATION_BUTTON_CANCEL',
        })?.afterClosed().subscribe(data => {
            if (data) {
                this.store.dispatch(new ShowLoader());
                this.cloudDataService.deleteOrgUnitSetting(this.orgUnitSettings[i].Id).subscribe(({success}) => {
                    if (success) {
                        this.getOrgUntSettings();
                    }

                    this.store.dispatch(new HideLoader());
                })
            }
        });
    }

    onSubmit(): void {
        if (!this.edittedOrgUnitSetting.Name?.trim()) {
            this.nameError = true;
        }

        if (this.nameError) {
            return;
        }

        this.edittedOrgUnitSetting.Description = this.edittedOrgUnitSetting.Name;

        this.store.dispatch(new ShowLoader());
        this.cloudDataService.saveOrgUnitSetting(this.edittedOrgUnitSetting).subscribe(({success}) => {
            if (success) {
                this.edittedOrgUnitSetting = null;
                this.getOrgUntSettings();
            }

            this.store.dispatch(new HideLoader());
        })
    }

    onSelectLeadTypes(values: string[]): void {
        this.selectedLeadTypes = this.leadTypes.filter(x => values.includes(`${x.Id}`));
        this.edittedOrgUnitSetting.AllowedLeadTypeIds = this.selectedLeadTypes.map(x => x.Id);

        this.updateSelectionTexts();
    }

    onSelectContactTypes(values: string[]): void {
        this.selectedContactTypes = this.contactTypes.filter(x => values.includes(`${x.Id}`));
        this.edittedOrgUnitSetting.DisallowedContactTypeIds = this.contactTypes.filter(x => !values.includes(`${x.Id}`)).map(x => x.Id);

        this.updateSelectionTexts();
    }

    updateSelectionTexts(): void {
        this.selectedLeadsText = this.selectedLeadTypes.map(x => x.Description).join(', ');
        this.selectedContactsText = this.selectedContactTypes.map(x => x.Description).join(', ');
        this.selectedParentText = this.orgUnitSettings.find(x => x.Id === this.edittedOrgUnitSetting.ParentId)?.Description;
    }

    onParentChange(value: string): void {
        const id = +value;
        this.edittedOrgUnitSetting.ParentId = id < 0 ? undefined : id;
        this.updateSelectionTexts();
    }

    setParentOptions(): void {
        this.parentDropdownOptions = [
            {
                Id: -1,
                Name: '',
                Description: '',
                DisallowedContactTypeIds: [],
                AllowedLeadTypeIds: [],
                IsFinstral: false,
                SharedRead: false,
                IsContactManagerOU: false,
                IsMandantAdminOU: false,
                MandantId: -1,
            },
            ...this.orgUnitSettings
        ].filter(x => x.Id !== this.edittedOrgUnitSetting?.Id);
    }

    back(): void {
        if (this.edittedOrgUnitSetting) {
            this.edittedOrgUnitSetting = null;
            return
        }
    }
}
